import { Box, Card, CardContent, CardHeader, Typography } from "@mui/material";
import { applicantName, type ResellerApplication } from "@xop/dashboard-shared";
import { Building2 } from "lucide-react";
import type { JSX } from "react";

import { formatDateTime } from "./format.js";

/** The application EXACTLY as the applicant typed it (spec §4.1: "kept
 * verbatim; never overwritten by a link"). The matcher's derived fields
 * (domains) are shown muted so a reviewer can see what the candidates were
 * scored on. */
export function ApplicationSummaryCard({ application: a }: { application: ResellerApplication }): JSX.Element {
  const website = a.website
    ? a.website.startsWith("http") ? a.website : `https://${a.website}`
    : null;
  const rows: [string, JSX.Element | string][] = [
    ["Company", a.company_name],
    [
      "Website",
      website ? (
        <Box component="a" href={website} target="_blank" rel="noreferrer" sx={{ color: "primary.main" }}>
          {a.website}
        </Box>
      ) : (
        "—"
      ),
    ],
    ["Contact", applicantName(a)],
    ["Email", a.email],
    ["Phone", a.phone ?? "—"],
    ["Country", a.country ?? "—"],
    ["Registered", formatDateTime(a.created_at)],
    // "Documents submitted at" rather than the bare status label ("Documents
    // submitted") — the status chip in the page header already renders that
    // exact string for this application's status, and a duplicate literal
    // text node breaks any `getByText` lookup that isn't scoped.
    ["Documents submitted at", formatDateTime(a.documents_submitted_at)],
  ];

  return (
    <Card data-cy="application-summary">
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "1.125rem", fontWeight: 600 }}>
            <Box sx={{ color: "text.secondary", display: "flex" }}><Building2 size={20} /></Box>
            <span>Application</span>
          </Box>
        }
      />
      <CardContent>
        <Box component="dl" sx={{ display: "grid", gridTemplateColumns: "max-content 1fr", columnGap: 3, rowGap: 1, m: 0, fontSize: 14 }}>
          {rows.map(([label, value]) => (
            <Box key={label} sx={{ display: "contents" }}>
              <Box component="dt" sx={{ color: "text.secondary" }}>{label}</Box>
              <Box component="dd" sx={{ m: 0, color: "text.primary" }}>{value}</Box>
            </Box>
          ))}
        </Box>
        {a.message && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: "surfaceSoft", borderRadius: 1 }}>
            <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}>Anything we should know</Typography>
            <Typography sx={{ fontSize: 14, whiteSpace: "pre-wrap" }}>{a.message}</Typography>
          </Box>
        )}
        <Typography sx={{ mt: 2, fontSize: 12, color: "text.disabled" }}>
          Matched on: email domain {a.email_domain ?? "—"} · website domain {a.website_domain ?? "—"}
        </Typography>
      </CardContent>
    </Card>
  );
}
